import { Link } from 'react-router-dom';
import { isClosed, EVENT_STATUSES } from '../events/labels';
import { entryNavigateUrl, type MyAssignment } from './MyCalendarPage';
import { ASSIGNMENT_STATUSES, hoursText } from './types';

const DATE = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' });
const CLOCK = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit' });
const endOf = (a: MyAssignment) => a.endsAt ?? a.event.endsAt;
const startOf = (a: MyAssignment) => a.startsAt ?? a.event.startsAt;

/** The entries still to come or going on (`now` before their end), in date order; those without visible dates last. */
export function upcoming(list: readonly MyAssignment[], now: Date): MyAssignment[] {
  return list
    .filter((a) => !endOf(a) || new Date(endOf(a)!).getTime() > now.getTime())
    .sort((a, b) => (startOf(a) ?? '￿').localeCompare(startOf(b) ?? '￿') || a.id.localeCompare(b.id));
}

/**
 * Owner 2026-10-30 21:38 („najpierw są wszystkie wydarzenia, w jakich osoba bierze udział”) / 21:49: every event I take
 * part in from today on, in date order — its dates and hours, my function, my status, „Wyjazd”, „Nawiguj” and the
 * name as a link when I may open it. Past events and those I was removed from are not here.
 */
export function MyEvents({ assignments, now }: { assignments: readonly MyAssignment[]; now: Date }) {
  const list = upcoming(assignments, now);
  return (
    <section className="mc-list" aria-labelledby="mc-events-title" data-feature-id="crew.my_calendar">
      <h2 id="mc-events-title">Moje wydarzenia</h2>
      {list.length === 0 ? (
        <p className="muted">Nie masz wydarzeń od dziś.</p>
      ) : (
        <ul className="my-events" aria-label="Moje wydarzenia">
          {list.map((a) => {
            const name = a.event.name || 'Wydarzenie';
            const cancelled = isClosed(a.event.status);
            const go = cancelled ? null : entryNavigateUrl(a);
            const start = startOf(a);
            return (
              <li key={`${a.id}:${a.startsAt ?? ''}`} className={`my-item${cancelled ? ' cancelled' : ''}`}>
                <span className="my-item-name">{a.canOpen ? <Link to={`/wydarzenia?wydarzenie=${a.event.id}`}>{name}</Link> : name}</span>
                <span className="my-item-meta">
                  {start && <span>{DATE.format(new Date(start))}</span>}
                  {a.startsAt && <span>{a.wholeEvent ? hoursText(a.startsAt, a.endsAt) : `${hoursText(a.startsAt, a.endsAt)} (część wydarzenia)`}</span>}
                  {a.functionName && <span>{a.functionName}</span>}
                  {a.status && <span className="my-event-status" data-status={a.status}>{ASSIGNMENT_STATUSES[a.status]}</span>}
                  {a.event.location && <span>{a.event.location}</span>}
                  {cancelled && a.event.status && <span>{EVENT_STATUSES[a.event.status]}</span>}
                </span>
                {!cancelled && (a.departureAt || go) && (
                  <span className="my-item-meta my-item-travel" data-feature-id="schedule.departure">
                    {a.departureAt && <strong>Wyjazd {CLOCK.format(new Date(a.departureAt))}</strong>}
                    {go && (
                      <a className="btn small" href={go} target="_blank" rel="noreferrer" data-feature-id="events.route">
                        Nawiguj
                      </a>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
